import { createContext, useContext, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useQuery } from "react-query";
import { internalApi } from "@/react/helpers/urlHelpers";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";

interface ProgressItem {
  id: number;
  progress: number;
  status: string;
}

interface ProgressContextValue {
  isLoading: boolean;
  snippetProgresses: ProgressItem[];
  spaceProgresses: ProgressItem[];
  totalProgress: number;
}

interface FormData {
  snippet_ids: number[];
  space_ids: number[];
}

interface ProgressResponse {
  progress: number;
  snippets: Array<{ id: number; progress: number; status: string }>;
  spaces: Array<{ id: number; progress: number; status: string }>;
}

const POLLING_INTERVAL = 800;

const ProgressContext = createContext<ProgressContextValue | null>(null);
ProgressContext.displayName = "ProgressContext";

export const useProgress = () => {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error("useProgress must be used within a ProgressProvider");
  }
  return context;
};

interface ProgressProviderProps {
  children: React.ReactNode;
  communityBotProfileId: number;
}

export const ProgressProvider = ({
  children,
  communityBotProfileId,
}: ProgressProviderProps) => {
  const { watch } = useFormContext<FormData>();
  const space_ids = watch("space_ids") || [];
  const snippet_ids = watch("snippet_ids") || [];

  const { data, isError } = useQuery<ProgressResponse>(
    ["progress", communityBotProfileId, space_ids, snippet_ids],
    () =>
      reactQueryGet(
        internalApi.communityBot.resources.progress({
          params: { spaceIds: space_ids, snippetIds: snippet_ids },
        }),
      ),
    {
      enabled: !!(
        communityBotProfileId &&
        (space_ids.length || snippet_ids.length)
      ),
      refetchInterval: data =>
        data?.progress === 100 ? false : POLLING_INTERVAL,
      staleTime: 0,
    },
  );

  const value = useMemo(() => {
    if (isError) {
      return {
        totalProgress: 100,
        isLoading: false,
        spaceProgresses: [],
        snippetProgresses: [],
      };
    }

    const spaceProgresses = data?.spaces || [];
    const snippetProgresses = data?.snippets || [];

    const totalItems = spaceProgresses.length + snippetProgresses.length;
    if (totalItems === 0)
      return {
        totalProgress: 100,
        isLoading: false,
        spaceProgresses,
        snippetProgresses,
      };

    const totalProgress = data?.progress ?? 100;

    const isLoading = totalProgress !== 100;

    return {
      totalProgress,
      isLoading,
      spaceProgresses,
      snippetProgresses,
    };
  }, [data, isError]);

  return (
    <ProgressContext.Provider value={value}>
      {children}
    </ProgressContext.Provider>
  );
};
